import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { TagSearch } from '@components/core-ui/Tag/variations/TagSearch';
import { TagSearchActive } from '@components/core-ui/Tag/variations/TagSearchActive';
import { ReactNode, useEffect, useState } from 'react';
import CloseIcon from '@components/icons/Close.svg';
import SearchIcon from '@components/icons/Search.svg';
import { Input } from '@components/core-ui/form-elements/Input';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { OrganizationsService } from 'app/API';

import { JobsList } from './components/Jobs';
import { UsersList } from './components/Users';
import { WorkspacesList } from './components/Workspaces';

import { Wrapper } from './styles/Wrapper';
import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';

function SearchScopeChip({
  selected,
  onClick,
  className,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  className?: string;
  children: ReactNode;
}) {
  return selected ? (
    <TagSearchActive className={className} onClick={onClick}>
      {children}
    </TagSearchActive>
  ) : (
    <TagSearch className={className} onClick={onClick}>
      {children}
    </TagSearch>
  );
}

export const SearchModal = () => {
  const { call } = useAPI();
  const { closeModal } = useModalContext();
  const { organization } = useAuthContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [tags, setTags] = useState<string[]>([]);
  const [search, setSearch] = useState<string>('');
  const [users, setUsers] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const canManageMembers = !!organization?.permissions?.manageMembers;

  const fetch = async () => {
    setLoading(true);
    const results = await call(OrganizationsService.search({ oid: organization?.id ?? '', search }));
    setJobs(results.jobs);
    setUsers(results.users);
    setWorkspaces(
      (organization?.workspaces ?? []).filter((ws) => ws.name.toLowerCase().includes(search.toLowerCase())),
    );
    setLoading(false);
  };

  useEffect(() => {
    if (search.length === 0) {
      setJobs([]);
      setUsers([]);
      setWorkspaces([]);
      setLoading(false);
      return;
    }
    fetch();
  }, [organization, search, tags]);

  const checkTag = (tag: string) => {
    if (tags.includes(tag)) {
      setTags([...tags.filter((t) => t !== tag)]);
    } else {
      setTags([...tags, tag]);
    }
  };

  return (
    <Wrapper>
      <div className="head">
        <SearchIcon className="search-icon" />
        <Input value={search} className="input" onChange={(e) => setSearch(e.target.value)} label="Search" />
        <Button className="clear-button" onClick={() => setSearch('')}>
          Clear
        </Button>
        <IconButtonClean className="close-button" onClick={() => closeModal()}>
          <CloseIcon />
        </IconButtonClean>
      </div>
      <div className="head-divider" />
      <div className="tags">
        <h4>{"I'm searching for:"}</h4>
        <SearchScopeChip selected={tags.length === 0} className="max-w-[150px]" onClick={() => setTags([])}>
          All
        </SearchScopeChip>
        <SearchScopeChip selected={tags.includes('job')} className="max-w-[150px]" onClick={() => checkTag('job')}>
          Job
        </SearchScopeChip>
        {canManageMembers ? (
          <SearchScopeChip selected={tags.includes('user')} className="max-w-[150px]" onClick={() => checkTag('user')}>
            User
          </SearchScopeChip>
        ) : null}
        <SearchScopeChip
          selected={tags.includes('workspace')}
          className="max-w-[150px]"
          onClick={() => checkTag('workspace')}
        >
          Workspace
        </SearchScopeChip>
      </div>
      <div className="results">
        {loading ? <AbsoluteSpinner /> : null}
        {search.length > 0 ? (
          <>
            {jobs.length > 0 && (!tags.length || tags.includes('job')) && <JobsList jobs={jobs} />}
            {users.length > 0 && canManageMembers && (!tags.length || tags.includes('user')) && (
              <UsersList users={users} />
            )}
            {workspaces.length > 0 && (!tags.length || tags.includes('workspace')) && (
              <WorkspacesList workspaces={workspaces} />
            )}
          </>
        ) : null}
      </div>
    </Wrapper>
  );
};
